import { useRef, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { Analyzer, Snapshot } from 'types';
import { ChartSnapshot } from 'features/spectrumChart/types';
import { parse, differenceInSeconds } from 'date-fns';

interface Props {
  initialTimestamp: string | undefined;
  analyzer: Analyzer | undefined;
  snapshot: Snapshot | ChartSnapshot | undefined;
  inHistory: boolean;
}

export const useTimestamp = ({
  initialTimestamp,
  analyzer,
  snapshot,
  inHistory,
}: Props) => {
  const { time_warning } = analyzer || {};
  const timestamp = useRef<string>('');
  const disabled = useRef<boolean>(false);
  const prevTimestamp = useRef<string>('');
  const timeWarningColor = useRef<string>('#32CD32');

  useEffect(() => {
    //First check to see if analyzer mode is on LIVE and we're looking at the correct snapshot. If true, we display "Disabled"
    if (analyzer?.mode === 0 && analyzer.active_snapshot !== snapshot?.id) {
      disabled.current = true;
      return;
    } else if (analyzer?.mode === 1 && !snapshot?.enabled) {
      //Second check to see if analyzer mode is on polling and the snapshot is actually enabled
      disabled.current = true;
      return;
    }
    disabled.current = false;
  }, [
    analyzer?.mode,
    analyzer?.active_snapshot,
    snapshot?.id,
    snapshot?.enabled,
  ]);

  if (timestamp.current === format(new Date(), 'MM/dd/yyyy hh:mm:ss aa')) {
    //If the component reruns after just comparing values and nothing has changed yet, we don't want prevTimeStamp overwritten.
    return [timestamp.current, timeWarningColor.current];
  }

  //We initialize the prevTimeStamp with the first timestamp that enters the hook. After, we will update this value based on timestamp.current.
  if (initialTimestamp && prevTimestamp.current !== timestamp.current) {
    prevTimestamp.current = format(
      parseISO(initialTimestamp),
      'MM/dd/yyyy hh:mm:ss aa'
    );
  }

  const currentDateTime = new Date();
  const lastUpdatedDate = parse(
    prevTimestamp.current,
    'MM/dd/yyyy hh:mm:ss aa',
    new Date()
  );
  const lastUpdatedSeconds = differenceInSeconds(
    currentDateTime,
    lastUpdatedDate
  );

  if (time_warning) {
    if (lastUpdatedSeconds >= time_warning?.critical_threshold) {
      timeWarningColor.current = '#FF505F';
    } else if (lastUpdatedSeconds >= time_warning?.warn_threshold) {
      timeWarningColor.current = '#F1A204';
    } else {
      timeWarningColor.current = '#32CD32';
    }
  }

  if (!snapshot || !analyzer || inHistory) {
    timestamp.current = '';
  } else {
    timestamp.current = disabled.current
      ? 'Deactivated'
      : format(new Date(), 'MM/dd/yyyy hh:mm:ss aa');

    prevTimestamp.current = timestamp.current;
  }

  return [timestamp.current, timeWarningColor.current];
};
